import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import type {
  Token,
  TokenPrice,
  Candle,
  UTBotSignal,
  UTBotConfig,
  ChartPanel,
  Theme,
  LayoutMode,
  TimeframeKey,
  UserSettings,
  NewsItem,
  AlertSettings,
  Order,
  Position,
  WalletInfo,
  PaperAccount,
  TradeMode,
  AutoTradeConfig,
  AutoTradeRecord,
  EquityPoint,
  LiveAccount,
  PriceAlert,
  RiskSettings,
  CloseReason,
} from '@arc/shared';
// LayoutItem is the per-panel shape used by react-grid-layout
export interface LayoutItem { i: string; x: number; y: number; w: number; h: number; minW?: number; maxW?: number; minH?: number; maxH?: number; }
import {
  DEFAULT_TOKENS,
  DEFAULT_UTBOT_CONFIG,
  CHART_CANDLE_LIMIT,
  SIGNAL_HISTORY_LIMIT,
} from '@arc/shared';
import { generateId } from '@arc/shared';
import { patchLiveCandles } from '../utils/liveCandles.js';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PriceMap  { [symbol: string]: TokenPrice }
interface CandleMap { [key: string]: Candle[] }       // `${symbol}:${timeframe}`
interface SignalMap  { [key: string]: UTBotSignal[] }  // `${symbol}:${timeframe}`

const DEFAULT_ALERT_SETTINGS: AlertSettings = {
  soundEnabled:          true,
  notificationsEnabled:  false,
  alertOnBuy:            true,
  alertOnSell:           true,
  minTimeframeSeverity:  '15m',
};

interface TerminalState {
  // Market data
  tokens:      Token[];
  prices:      PriceMap;
  /** USD market cap per symbol (CoinGecko, circulating supply). Not persisted. */
  marketCaps:  Record<string, number>;
  candles:     CandleMap;
  signals:     SignalMap;
  news:        NewsItem[];

  // UI state
  activeSymbol:      string;
  activePanels:      ChartPanel[];
  /** Index into activePanels of the chart watchlist clicks load into. */
  focusedPanelIndex: number;
  theme:             Theme;
  layout:            LayoutMode;
  isBootComplete:    boolean;
  wsConnected:       boolean;
  /** Per `${symbol}:${tf}` candle feed status (not persisted). */
  feedStatus:        Record<string, FeedStatus>;
  /** Bumped to force candle loaders to re-fetch (retry). */
  feedRetryNonce:    number;
  showAlertSettings: boolean;
  panelLayouts:      LayoutItem[];
  /** Grid panels the user removed (persisted; restored from the grid's action bar). */
  hiddenPanelIds:    string[];
  uiScale:           number;   // zoom factor: 0.85 | 1 | 1.15 | 1.3
  dbSyncStatus:      'idle' | 'loading' | 'ready' | 'error';
  dbBackend:         string;

  // User settings
  settings: UserSettings;

  // ── Trading state ───────────────────────────────────────────────────────────
  tradeMode:    TradeMode;
  orders:       Order[];
  positions:    Position[];
  wallets:      WalletInfo[];
  activeWallet: WalletInfo | null;
  paperAccount: PaperAccount;
  liveAccount:  LiveAccount | null;   // Real wallet equity (refreshed, not persisted)

  // ── Price Alerts ─────────────────────────────────────────────────────────────
  priceAlerts:  PriceAlert[];

  // ── Risk Management ──────────────────────────────────────────────────────────
  riskSettings:         RiskSettings;
  circuitBreakerActive: boolean;

  // ── Auto-trade state ─────────────────────────────────────────────────────────
  autoTradeConfig:  AutoTradeConfig;
  autoTradeRecords: AutoTradeRecord[];
  equityCurve:      EquityPoint[];

  // ── Actions ────────────────────────────────────────────────────────────────
  setDbSync:             (status: 'idle' | 'loading' | 'ready' | 'error', backend?: string) => void;
  setBootComplete:       () => void;
  setUiScale:            (scale: number) => void;
  setTheme:              (theme: Theme) => void;
  setLayout:             (layout: LayoutMode) => void;
  setActiveSymbol:       (symbol: string) => void;
  focusPanel:            (index: number) => void;
  setWSConnected:        (connected: boolean) => void;
  setFeedStatus:         (symbol: string, tf: TimeframeKey, status: FeedStatus) => void;
  retryFeeds:            () => void;
  setShowAlertSettings:  (show: boolean) => void;
  setPanelLayouts:       (layouts: LayoutItem[]) => void;
  hidePanel:             (id: string) => void;
  showPanel:             (id: string) => void;
  clearHiddenPanels:     () => void;

  updatePrice:           (price: TokenPrice) => void;
  updatePrices:          (prices: TokenPrice[]) => void;
  setMarketCaps:         (caps: Record<string, number>) => void;
  pushCandle:            (symbol: string, tf: TimeframeKey, candle: Candle) => void;
  setCandles:            (symbol: string, tf: TimeframeKey, candles: Candle[]) => void;
  pushSignal:            (signal: UTBotSignal) => void;
  setNews:               (items: NewsItem[]) => void;

  addPanelSymbol:        (symbol: string) => void;
  ensurePanelCount:      (count: number) => void;
  removePanelSymbol:     (panelId: string) => void;
  setPanelConfig:        (panelId: string, config: Partial<UTBotConfig>) => void;
  setPanelTimeframe:     (panelId: string, tf: TimeframeKey) => void;
  setPanelSymbol:        (panelId: string, symbol: string) => void;

  addToken:              (token: Token) => void;
  removeToken:           (symbol: string) => void;
  updateSettings:        (settings: Partial<UserSettings>) => void;
  /** Bot UT Bot settings (settings.defaultConfig), separate from each chart's. */
  setBotConfig:          (config: Partial<Pick<UTBotConfig, 'keyValue' | 'atrPeriod'>>) => void;
  updateAlertSettings:   (settings: Partial<AlertSettings>) => void;
  addToWatchlist:        (symbol: string) => void;
  removeFromWatchlist:   (symbol: string) => void;

  // Auto-trade actions
  setAutoTradeConfig:     (cfg: Partial<AutoTradeConfig>) => void;
  addAutoTradeRecord:     (record: AutoTradeRecord) => void;
  closeAutoTradeRecord:   (id: string, exitPrice: number, exitSignalId?: string) => void;
  resetAutoTrade:         () => void;

  // Trading actions
  setTradeMode:          (mode: TradeMode) => void;
  placeOrder:            (order: Order) => void;
  fillOrder:             (orderId: string, fillPrice: number) => void;
  cancelOrder:           (orderId: string) => void;
  openPosition:          (position: Position) => void;
  closePosition:         (positionId: string, closePrice: number) => void;
  updatePositionPrices:  (prices: PriceMap) => void;
  connectWallet:         (wallet: WalletInfo) => void;
  disconnectWallet:      (address: string) => void;
  setActiveWallet:       (wallet: WalletInfo | null) => void;
  resetPaperAccount:     () => void;
  setLiveAccount:        (account: LiveAccount | null) => void;
  setPositionSLTP:       (positionId: string, sl: number | undefined, tp: number | undefined) => void;
  checkAndClosePositions:(prices: PriceMap) => void;   // combined price update + SL/TP check

  // Price alert actions
  addPriceAlert:         (alert: PriceAlert) => void;
  removePriceAlert:      (id: string) => void;
  firePriceAlert:        (id: string) => void;

  // Risk actions
  setRiskSettings:       (settings: Partial<RiskSettings>) => void;
  setCircuitBreaker:     (active: boolean) => void;

  // DB hydration — bulk setters used by useDbSync on startup
  setOrders:             (orders: Order[]) => void;
  setPriceAlerts:        (alerts: PriceAlert[]) => void;
  setEquityCurve:        (curve: EquityPoint[]) => void;
  setAutoTradeRecords:   (records: AutoTradeRecord[]) => void;
  setPaperAccount:       (account: Partial<PaperAccount>) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export type FeedStatus = 'loading' | 'ok' | 'empty' | 'error';

const BOOT_SEEN_KEY = 'arc:boot-seen';
function hasSeenBoot(): boolean {
  try { return sessionStorage.getItem(BOOT_SEEN_KEY) === '1'; } catch { return false; }
}

const PAPER_STARTING_BALANCE = 10_000;

const DEFAULT_AUTO_TRADE_CONFIG: AutoTradeConfig = {
  enabled:      false,
  symbols:      [],           // all symbols
  sizeUsd:      500,          // $500 per trade (5% of $10k)
  leverage:     1,
  mode:         'long_only',
  maxPositions: 3,
  timeframes:   [],           // all timeframes
};

const DEFAULT_PAPER_ACCOUNT: PaperAccount = {
  balance:     PAPER_STARTING_BALANCE,
  equity:      PAPER_STARTING_BALANCE,
  totalPnl:    0,
  winTrades:   0,
  lossTrades:  0,
  totalTrades: 0,
};

const DEFAULT_RISK_SETTINGS: RiskSettings = {
  maxDailyLossPct:        5,
  defaultRiskPerTradePct: 1,
  maxOpenPositions:       10,
  circuitBreakerEnabled:  true,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function makeDefaultPanels(): ChartPanel[] {
  return DEFAULT_TOKENS.slice(0, 2).map(token => ({
    id:        generateId(),
    symbol:    token.symbol,
    timeframe: DEFAULT_UTBOT_CONFIG.timeframe,
    config:    { ...DEFAULT_UTBOT_CONFIG },
    candles:   [],
    signals:   [],
  }));
}

function candleKey(symbol: string, tf: TimeframeKey): string {
  return `${symbol}:${tf}`;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useTerminalStore = create<TerminalState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // ── Initial State ────────────────────────────────────────────────────
        tokens:         DEFAULT_TOKENS,
        prices:         {},
        marketCaps:     {},
        candles:        {},
        signals:        {},
        news:           [],
        activeSymbol:   DEFAULT_TOKENS[0]?.symbol ?? 'BTC',
        activePanels:   makeDefaultPanels(),
        focusedPanelIndex: 0,
        theme:          'arc',
        layout:         'quad',
        isBootComplete:    hasSeenBoot(),
        wsConnected:       false,
        feedStatus:        {},
        feedRetryNonce:    0,
        showAlertSettings: false,
        panelLayouts:      [],
        hiddenPanelIds:    [],
        uiScale:           1.2,
        dbSyncStatus:      'idle',
        dbBackend:         'sqlite',
        settings: {
          theme:         'arc',
          layout:        'quad',
          watchlist:     DEFAULT_TOKENS.map(t => t.symbol),
          defaultConfig: { ...DEFAULT_UTBOT_CONFIG },
          alerts:        { ...DEFAULT_ALERT_SETTINGS },
          customTokens:  [],
        },

        // ── Trading initial state ────────────────────────────────────────────
        tradeMode:    'paper',
        orders:       [],
        positions:    [],
        wallets:      [],
        activeWallet: null,
        paperAccount: { ...DEFAULT_PAPER_ACCOUNT },
        liveAccount:  null,
        priceAlerts:  [],
        riskSettings: { ...DEFAULT_RISK_SETTINGS },
        circuitBreakerActive: false,

        // ── Auto-trade initial state ─────────────────────────────────────────
        autoTradeConfig:  { ...DEFAULT_AUTO_TRADE_CONFIG },
        autoTradeRecords: [],
        equityCurve:      [{ timestamp: Date.now(), equity: PAPER_STARTING_BALANCE }],

        // ── Boot / Connection ────────────────────────────────────────────────
        setDbSync: (status, backend) => set(backend !== undefined ? { dbSyncStatus: status, dbBackend: backend } : { dbSyncStatus: status }),
        setBootComplete:      () => {
          try { sessionStorage.setItem(BOOT_SEEN_KEY, '1'); } catch { /* storage unavailable */ }
          set({ isBootComplete: true });
        },
        setUiScale: (scale) => set({ uiScale: scale }),
        setWSConnected:       (connected) => set({ wsConnected: connected }),
        setFeedStatus:        (symbol, tf, status) =>
          set(state => ({ feedStatus: { ...state.feedStatus, [candleKey(symbol, tf)]: status } })),
        retryFeeds:           () => set(state => ({ feedRetryNonce: state.feedRetryNonce + 1 })),
        setShowAlertSettings: (show) => set({ showAlertSettings: show }),
        setPanelLayouts:      (layouts) => set({ panelLayouts: layouts }),
        hidePanel:            (id) => set(state => state.hiddenPanelIds.includes(id) ? state : { hiddenPanelIds: [...state.hiddenPanelIds, id] }),
        showPanel:            (id) => set(state => ({ hiddenPanelIds: state.hiddenPanelIds.filter(h => h !== id) })),
        clearHiddenPanels:    () => set({ hiddenPanelIds: [] }),

        // ── Theme / Layout ───────────────────────────────────────────────────
        setTheme: (theme) => {
          document.documentElement.setAttribute('data-theme', theme);
          set({ theme, settings: { ...get().settings, theme } });
        },
        setLayout: (layout) =>
          set({ layout, settings: { ...get().settings, layout } }),

        // ── Symbol Selection ─────────────────────────────────────────────────
        // Also loads the symbol into the focused chart (falls back to the first
        // if the focused index no longer exists).
        setActiveSymbol: (symbol) => set(state => {
          const target = state.focusedPanelIndex < state.activePanels.length ? state.focusedPanelIndex : 0;
          return {
            activeSymbol: symbol,
            activePanels: state.activePanels.map((p, i) =>
              i === target ? { ...p, symbol } : p
            ),
          };
        }),
        focusPanel: (index) => set(state => state.focusedPanelIndex === index ? state : { focusedPanelIndex: index }),

        // ── Price Updates ────────────────────────────────────────────────────
        updatePrice: (price) =>
          set(state => ({
            prices:  { ...state.prices, [price.symbol]: price },
            candles: patchLiveCandles(state.candles, [price]),
          })),

        updatePrices: (incoming) =>
          set(state => {
            const updated = { ...state.prices };
            for (const p of incoming) updated[p.symbol] = p;
            return { prices: updated, candles: patchLiveCandles(state.candles, incoming) };
          }),

        setMarketCaps: (caps) =>
          set(state => ({ marketCaps: { ...state.marketCaps, ...caps } })),

        // ── Candle Management ────────────────────────────────────────────────
        setCandles: (symbol, tf, candles) =>
          set(state => {
            const p = state.prices[symbol];
            const next = { ...state.candles, [candleKey(symbol, tf)]: candles.slice(-CHART_CANDLE_LIMIT) };
            // Fold in a ticker that is newer than the loaded history's last close
            return { candles: p ? patchLiveCandles(next, [p]) : next };
          }),

        pushCandle: (symbol, tf, candle) =>
          set(state => {
            const key      = candleKey(symbol, tf);
            const existing = state.candles[key] ?? [];
            const last     = existing[existing.length - 1];

            if (!last || candle.time > last.time) {
              return { candles: { ...state.candles, [key]: [...existing, candle].slice(-CHART_CANDLE_LIMIT) } };
            }
            // Same or older slot: replace it in place. A server candle can arrive
            // after a live tick already opened the next slot; appending it would
            // put the series out of time order, which the chart rejects.
            const idx = existing.findIndex(c => c.time === candle.time);
            if (idx === -1) return state;
            const next = existing.slice();
            next[idx] = candle;
            return { candles: { ...state.candles, [key]: next } };
          }),

        // ── Signal Management ────────────────────────────────────────────────
        pushSignal: (signal) =>
          set(state => {
            const key      = candleKey(signal.symbol, signal.timeframe);
            const existing = state.signals[key] ?? [];
            if (existing.some(s => s.id === signal.id)) return state;
            return { signals: { ...state.signals, [key]: [signal, ...existing].slice(0, SIGNAL_HISTORY_LIMIT) } };
          }),

        // ── News ─────────────────────────────────────────────────────────────
        setNews: (items) => set({ news: items }),

        // ── Panel Management ─────────────────────────────────────────────────
        addPanelSymbol: (symbol) =>
          set(state => ({
            activePanels: [
              ...state.activePanels,
              {
                id:        generateId(),
                symbol,
                timeframe: DEFAULT_UTBOT_CONFIG.timeframe,
                config:    { ...DEFAULT_UTBOT_CONFIG },
                candles:   [],
                signals:   [],
              },
            ],
          })),

        ensurePanelCount: (count) =>
          set(state => {
            const curr = state.activePanels;
            if (curr.length >= count) return {};
            const SYMS = ['BTC', 'ETH', 'SOL', 'BNB', 'WIF', 'JTO'];
            const toAdd = Array.from({ length: count - curr.length }, (_, i) => ({
              id:        generateId(),
              symbol:    SYMS[(curr.length + i) % SYMS.length] ?? 'BTC',
              timeframe: DEFAULT_UTBOT_CONFIG.timeframe,
              config:    { ...DEFAULT_UTBOT_CONFIG },
              candles:   [],
              signals:   [],
            }));
            return { activePanels: [...curr, ...toAdd] };
          }),

        removePanelSymbol: (panelId) =>
          set(state => ({ activePanels: state.activePanels.filter(p => p.id !== panelId) })),

        setPanelConfig: (panelId, config) =>
          set(state => ({
            activePanels: state.activePanels.map(p =>
              p.id === panelId ? { ...p, config: { ...p.config, ...config } } : p
            ),
          })),

        setPanelTimeframe: (panelId, tf) =>
          set(state => ({
            activePanels: state.activePanels.map(p =>
              p.id === panelId
                ? { ...p, timeframe: tf, config: { ...p.config, timeframe: tf } }
                : p
            ),
          })),

        setPanelSymbol: (panelId, symbol) =>
          set(state => ({
            activePanels: state.activePanels.map(p =>
              p.id === panelId ? { ...p, symbol } : p
            ),
          })),

        // ── Token Management ─────────────────────────────────────────────────
        addToken: (token) =>
          set(state => {
            // Avoid duplicates. Compare addresses only when both tokens have one:
            // symbol-search results and BTC/ETH/BNB have none, and undefined ===
            // undefined would reject every such add. Exact match (Solana
            // addresses are case-sensitive).
            if (state.tokens.find(t =>
              t.symbol === token.symbol ||
              (token.address !== undefined && t.address === token.address)
            )) {
              return state;
            }
            const newTokens  = [...state.tokens, token];
            const customList = [...(state.settings.customTokens ?? []), token];
            return {
              tokens:   newTokens,
              settings: {
                ...state.settings,
                customTokens: customList,
                watchlist: [...state.settings.watchlist, token.symbol],
              },
            };
          }),

        removeToken: (symbol) =>
          set(state => {
            const watchlist = state.settings.watchlist.filter(s => s !== symbol);
            const tokens    = state.tokens.filter(t => t.symbol !== symbol);
            // Charts (and the active symbol) still pointing at the removed token
            // would be left with no token to load; move them to a remaining one.
            const fallback  = watchlist[0] ?? tokens[0]?.symbol ?? 'BTC';
            return {
              tokens,
              activeSymbol: state.activeSymbol === symbol ? fallback : state.activeSymbol,
              activePanels: state.activePanels.map(p => p.symbol === symbol ? { ...p, symbol: fallback } : p),
              settings: {
                ...state.settings,
                customTokens: (state.settings.customTokens ?? []).filter(t => t.symbol !== symbol),
                watchlist,
              },
            };
          }),

        // ── Settings ─────────────────────────────────────────────────────────
        updateSettings: (partial) =>
          set(state => ({ settings: { ...state.settings, ...partial } })),

        setBotConfig: (config) =>
          set(state => ({
            settings: { ...state.settings, defaultConfig: { ...state.settings.defaultConfig, ...config } },
          })),

        updateAlertSettings: (partial) =>
          set(state => ({
            settings: {
              ...state.settings,
              alerts: { ...state.settings.alerts, ...partial },
            },
          })),

        addToWatchlist: (symbol) =>
          set(state => {
            if (state.settings.watchlist.includes(symbol)) return state;
            return {
              settings: { ...state.settings, watchlist: [...state.settings.watchlist, symbol] },
            };
          }),

        removeFromWatchlist: (symbol) =>
          set(state => ({
            settings: {
              ...state.settings,
              watchlist: state.settings.watchlist.filter(s => s !== symbol),
            },
          })),

        // ── Auto-trade actions ────────────────────────────────────────────────
        setAutoTradeConfig: (cfg) =>
          set(state => ({ autoTradeConfig: { ...state.autoTradeConfig, ...cfg } })),

        addAutoTradeRecord: (record) =>
          set(state => ({ autoTradeRecords: [record, ...state.autoTradeRecords].slice(0, 500) })),

        closeAutoTradeRecord: (id, exitPrice, exitSignalId) =>
          set(state => {
            const record = state.autoTradeRecords.find(r => r.id === id);
            if (!record) return state;
            const pnl = record.side === 'buy'
              ? (exitPrice - record.entryPrice) / record.entryPrice * record.sizeUsd
              : (record.entryPrice - exitPrice) / record.entryPrice * record.sizeUsd;
            const pnlPct = record.side === 'buy'
              ? (exitPrice - record.entryPrice) / record.entryPrice * 100
              : (record.entryPrice - exitPrice) / record.entryPrice * 100;
            const closedAt = Date.now();
            const updated: AutoTradeRecord = {
              ...record, status: 'closed', exitPrice, pnl, pnlPct,
              closedAt, holdingMs: closedAt - record.openedAt,
              ...(exitSignalId ? { exitSignalId } : {}),
            };
            const lastEquity = state.equityCurve.at(-1)?.equity ?? PAPER_STARTING_BALANCE;
            const newEquityPt: EquityPoint = { timestamp: closedAt, equity: lastEquity + pnl, tradeId: id };
            return {
              autoTradeRecords: state.autoTradeRecords.map(r => r.id === id ? updated : r),
              equityCurve:      [...state.equityCurve, newEquityPt].slice(-1000),
            };
          }),

        resetAutoTrade: () =>
          set({
            autoTradeRecords: [],
            equityCurve: [{ timestamp: Date.now(), equity: PAPER_STARTING_BALANCE }],
          }),

        // ── Trading actions ───────────────────────────────────────────────────
        setTradeMode: (mode) => set({ tradeMode: mode }),

        placeOrder: (order) =>
          set(state => ({ orders: [order, ...state.orders].slice(0, 200) })),

        fillOrder: (orderId, fillPrice) =>
          set(state => ({
            orders: state.orders.map(o =>
              o.id === orderId
                ? { ...o, status: 'filled' as const, fillPrice, closedAt: Date.now() }
                : o
            ),
          })),

        cancelOrder: (orderId) =>
          set(state => ({
            orders: state.orders.map(o =>
              o.id === orderId ? { ...o, status: 'cancelled' as const } : o
            ),
          })),

        openPosition: (position) =>
          set(state => ({ positions: [position, ...state.positions] })),

        closePosition: (positionId, closePrice) =>
          set(state => {
            const pos = state.positions.find(p => p.id === positionId);
            if (!pos) return state;

            const pnl = pos.side === 'buy'
              ? (closePrice - pos.entryPrice) / pos.entryPrice * pos.size * pos.leverage
              : (pos.entryPrice - closePrice) / pos.entryPrice * pos.size * pos.leverage;

            const isWin = pnl >= 0;
            const newBalance = state.paperAccount.balance + pos.size + pnl;

            return {
              positions: state.positions.filter(p => p.id !== positionId),
              paperAccount: {
                ...state.paperAccount,
                balance:     newBalance,
                equity:      newBalance,
                totalPnl:    state.paperAccount.totalPnl + pnl,
                winTrades:   state.paperAccount.winTrades  + (isWin ? 1 : 0),
                lossTrades:  state.paperAccount.lossTrades + (isWin ? 0 : 1),
                totalTrades: state.paperAccount.totalTrades + 1,
              },
              orders: state.orders.map(o =>
                o.id === pos.orderId
                  ? { ...o, pnl, closedAt: Date.now() }
                  : o
              ),
            };
          }),

        updatePositionPrices: (prices) =>
          set(state => {
            if (state.positions.length === 0) return state;
            let equity = state.paperAccount.balance;
            const updated = state.positions.map(pos => {
              const cur = prices[pos.symbol]?.priceUsd ?? pos.currentPrice;
              const pnl = pos.side === 'buy'
                ? (cur - pos.entryPrice) / pos.entryPrice * pos.size * pos.leverage
                : (pos.entryPrice - cur) / pos.entryPrice * pos.size * pos.leverage;
              const pct = pnl / (pos.size) * 100;
              equity += pnl;
              return { ...pos, currentPrice: cur, unrealizedPnl: pnl, unrealizedPnlPct: pct };
            });
            return {
              positions: updated,
              paperAccount: { ...state.paperAccount, equity },
            };
          }),

        // Combined price update + SL/TP auto-close in one atomic set()
        checkAndClosePositions: (prices) =>
          set(state => {
            if (state.positions.length === 0) return state;

            const toClose: Array<{ pos: Position; closePrice: number; reason: CloseReason }> = [];
            let equity = state.paperAccount.balance;

            const updatedPositions = state.positions.map(pos => {
              const cur = prices[pos.symbol]?.priceUsd ?? pos.currentPrice;
              const pnl = pos.side === 'buy'
                ? (cur - pos.entryPrice) / pos.entryPrice * pos.size * pos.leverage
                : (pos.entryPrice - cur) / pos.entryPrice * pos.size * pos.leverage;
              const pct = pnl / pos.size * 100;

              // Check SL/TP (paper mode only — live positions handled by exchange)
              if (pos.mode === 'paper') {
                if (pos.stopLoss !== undefined) {
                  const slHit = pos.side === 'buy' ? cur <= pos.stopLoss : cur >= pos.stopLoss;
                  if (slHit) { toClose.push({ pos, closePrice: pos.stopLoss, reason: 'stop_loss' }); return pos; }
                }
                if (pos.takeProfit !== undefined) {
                  const tpHit = pos.side === 'buy' ? cur >= pos.takeProfit : cur <= pos.takeProfit;
                  if (tpHit) { toClose.push({ pos, closePrice: pos.takeProfit, reason: 'take_profit' }); return pos; }
                }
              }

              equity += pnl;
              return { ...pos, currentPrice: cur, unrealizedPnl: pnl, unrealizedPnlPct: pct };
            });

            if (toClose.length === 0) {
              return { positions: updatedPositions, paperAccount: { ...state.paperAccount, equity } };
            }

            // Process closures
            let { balance, totalPnl, winTrades, lossTrades, totalTrades } = state.paperAccount;
            const closingIds = new Set(toClose.map(c => c.pos.id));
            const remainingPositions = updatedPositions.filter(p => !closingIds.has(p.id));
            let updatedOrders = state.orders;

            for (const { pos, closePrice, reason } of toClose) {
              const pnl = pos.side === 'buy'
                ? (closePrice - pos.entryPrice) / pos.entryPrice * pos.size * pos.leverage
                : (pos.entryPrice - closePrice) / pos.entryPrice * pos.size * pos.leverage;
              const isWin   = pnl >= 0;
              balance      += pos.size + pnl;
              totalPnl     += pnl;
              winTrades    += isWin ? 1 : 0;
              lossTrades   += isWin ? 0 : 1;
              totalTrades  += 1;
              updatedOrders = updatedOrders.map(o =>
                o.id === pos.orderId
                  ? { ...o, pnl, closedAt: Date.now(), closeReason: reason }
                  : o
              );
            }

            return {
              positions:    remainingPositions,
              orders:       updatedOrders,
              paperAccount: { ...state.paperAccount, balance, equity: balance, totalPnl, winTrades, lossTrades, totalTrades },
            };
          }),

        connectWallet: (wallet) =>
          set(state => {
            const existing = state.wallets.find(w => w.address === wallet.address);
            const wallets  = existing
              ? state.wallets.map(w => w.address === wallet.address ? wallet : w)
              : [...state.wallets, wallet];
            return { wallets, activeWallet: wallet };
          }),

        disconnectWallet: (address) =>
          set(state => {
            const wallets = state.wallets.filter(w => w.address !== address);
            const active  = state.activeWallet?.address === address ? (wallets[0] ?? null) : state.activeWallet;
            return { wallets, activeWallet: active };
          }),

        setActiveWallet: (wallet) => set({ activeWallet: wallet }),

        resetPaperAccount: () =>
          set({ paperAccount: { ...DEFAULT_PAPER_ACCOUNT }, orders: [], positions: [] }),

        setLiveAccount: (account) => set({ liveAccount: account }),

        setPositionSLTP: (positionId, sl, tp) =>
          set(state => ({
            positions: state.positions.map(p => {
              if (p.id !== positionId) return p;
              const updated: Position = { ...p };
              if (sl !== undefined) updated.stopLoss   = sl;
              else                  delete updated.stopLoss;
              if (tp !== undefined) updated.takeProfit = tp;
              else                  delete updated.takeProfit;
              return updated;
            }),
          })),

        // ── Price alerts ─────────────────────────────────────────────────────
        addPriceAlert: (alert) =>
          set(state => ({ priceAlerts: [alert, ...state.priceAlerts].slice(0, 50) })),

        removePriceAlert: (id) =>
          set(state => ({ priceAlerts: state.priceAlerts.filter(a => a.id !== id) })),

        firePriceAlert: (id) =>
          set(state => ({
            priceAlerts: state.priceAlerts
              .map(a => a.id === id ? { ...a, firedAt: Date.now() } : a)
              .filter(a => a.id !== id || a.repeat), // remove non-repeating after firing
          })),

        // ── Risk management ──────────────────────────────────────────────────
        setRiskSettings: (partial) =>
          set(state => ({ riskSettings: { ...state.riskSettings, ...partial } })),

        setCircuitBreaker: (active) => set({ circuitBreakerActive: active }),

        // ── DB hydration (bulk setters) ────────────────────────────────────
        setOrders:           (orders)  => set({ orders }),
        setPriceAlerts:      (alerts)  => set({ priceAlerts: alerts }),
        setEquityCurve:      (curve)   => set({ equityCurve: curve }),
        setAutoTradeRecords: (records) => set({ autoTradeRecords: records }),
        setPaperAccount:     (account) =>
          set(state => ({ paperAccount: { ...state.paperAccount, ...account } })),
      }),
      {
        name: 'arc-terminal-v6',
        partialize: (state) => ({
          theme:        state.theme,
          layout:       state.layout,
          activeSymbol: state.activeSymbol,
          settings:     state.settings,
          panelLayouts: state.panelLayouts,
          hiddenPanelIds: state.hiddenPanelIds,
          activePanels: state.activePanels,
          focusedPanelIndex: state.focusedPanelIndex,
          tradeMode:        state.tradeMode,
          paperAccount:     state.paperAccount,
          orders:           state.orders.slice(0, 50),
          uiScale:          state.uiScale,
          autoTradeConfig:  state.autoTradeConfig,
          autoTradeRecords: state.autoTradeRecords.slice(0, 200),
          equityCurve:      state.equityCurve.slice(-500),
          priceAlerts:      state.priceAlerts.slice(0, 50),
          riskSettings:     state.riskSettings,
        }),
        // Merge persisted settings with defaults so new fields aren't undefined
        merge: (persisted: unknown, current) => {
          const p = persisted as Partial<TerminalState>;
          return {
            ...current,
            ...p,
            activePanels: (p.activePanels && p.activePanels.length > 0)
              ? p.activePanels.map(panel => ({
                  ...panel,
                  config:   panel.config   ?? { ...DEFAULT_UTBOT_CONFIG },
                  candles:  panel.candles  ?? [],
                  signals:  panel.signals  ?? [],
                }))
              : current.activePanels,
            hiddenPanelIds: p.hiddenPanelIds ?? [],
            positions:    [],         // Never restore open positions (price may be stale)
            wallets:      [],         // Never restore wallet connections
            activeWallet: null,
            liveAccount:  null,
            circuitBreakerActive: false,
            priceAlerts:  p.priceAlerts ?? [],
            riskSettings: { ...DEFAULT_RISK_SETTINGS, ...(p.riskSettings ?? {}) },
            paperAccount: {
              ...DEFAULT_PAPER_ACCOUNT,
              ...(p.paperAccount ?? {}),
            },
            orders:           p.orders ?? [],
            autoTradeConfig:  { ...DEFAULT_AUTO_TRADE_CONFIG, ...(p.autoTradeConfig ?? {}) },
            autoTradeRecords: p.autoTradeRecords ?? [],
            equityCurve:      (p.equityCurve && p.equityCurve.length > 0)
              ? p.equityCurve
              : [{ timestamp: Date.now(), equity: PAPER_STARTING_BALANCE }],
            settings: {
              ...current.settings,
              ...(p.settings ?? {}),
              alerts: {
                ...DEFAULT_ALERT_SETTINGS,
                ...(p.settings?.alerts ?? {}),
              },
              customTokens: p.settings?.customTokens ?? [],
            },
            tokens: [
              ...DEFAULT_TOKENS,
              ...(p.settings?.customTokens ?? []).filter(
                ct => !DEFAULT_TOKENS.find(dt => dt.symbol === ct.symbol)
              ),
            ],
          };
        },
      }
    )
  )
);

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectActivePrice = (state: TerminalState) =>
  state.prices[state.activeSymbol];

// One shared empty array, so a symbol with no candles yet doesn't hand
// subscribers a new reference (and re-run their effects) on every store update.
const NO_CANDLES: Candle[] = Object.freeze([]) as unknown as Candle[];

export const selectCandles = (symbol: string, tf: TimeframeKey) =>
  (state: TerminalState) => state.candles[`${symbol}:${tf}`] ?? NO_CANDLES;

export const selectFeedStatus = (symbol: string, tf: TimeframeKey) =>
  (state: TerminalState): FeedStatus => state.feedStatus[`${symbol}:${tf}`] ?? 'loading';

/**
 * Single display-price source: the latest chart candle close (same value the
 * chart's price axis shows) when available, falling back to the polled spot price.
 */
export const selectDisplayPrice = (symbol: string, tf?: TimeframeKey) =>
  (state: TerminalState): number => {
    const key = tf
      ? `${symbol}:${tf}`
      : Object.keys(state.candles).find(k => k.startsWith(`${symbol}:`) && (state.candles[k]?.length ?? 0) > 0);
    const last = key ? state.candles[key]?.at(-1) : undefined;
    const tick = state.prices[symbol];
    // Ticker is newer than the last candle → use it so the header never freezes.
    if (tick && tick.priceUsd > 0 && (!last || (tick.timestamp ?? 0) >= last.time * 1000)) return tick.priceUsd;
    return last?.close ?? tick?.priceUsd ?? 0;
  };

export const selectSignals = (symbol: string, tf: TimeframeKey) =>
  (state: TerminalState) => state.signals[`${symbol}:${tf}`] ?? [];

export const selectAllSignals = (state: TerminalState): UTBotSignal[] => {
  const seen = new Set<string>();
  return Object.values(state.signals)
    .flat()
    .filter(s => { if (seen.has(s.id)) return false; seen.add(s.id); return true; })
    .sort((a, b) => b.timestamp - a.timestamp);
};

export const selectWatchlistTokens = (state: TerminalState) =>
  state.tokens.filter(t => state.settings.watchlist.includes(t.symbol));

export const selectAlertSettings = (state: TerminalState) =>
  state.settings.alerts;
